import { Component, OnInit } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { Router } from '@angular/router'

import { ApiAlgorithmConfigService } from '../../data/services/api/api-algorithm-config.service'
import { AlgorithmConfigStatus, IAlgorithmConfig } from '../../data/interfaces/algorithm-config.interface'
import { STATUS_LABEL, STATUS_SEVERITY } from '../../data/constants/algorithm-config.constants'

@Component({
  selector: 'app-algorithm-config-list',
  templateUrl: './algorithm-config-list.component.html',
  styleUrls: ['./algorithm-config-list.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class AlgorithmConfigListComponent implements OnInit {
  configs: IAlgorithmConfig[] = []
  active: IAlgorithmConfig | null = null
  cargando = true
  //! Un fallo de carga se veía IDÉNTICO a «no hay versiones»: la tabla salía vacía y el usuario no
  //! podía saber si la lista estaba vacía de verdad o si la petición había fallado. Son estados
  //! distintos y necesitan tratamiento distinto — este además ofrece reintentar.
  errorCarga: string | null = null

  //* Métodos y no acceso directo al Record desde el template: las filas de `p-table` llegan como
  //* `any`, y bajo `strict` un `any` no puede indexar un `Record<AlgorithmConfigStatus, string>`.
  etiquetaEstado(status: string): string {
    return STATUS_LABEL[status as AlgorithmConfigStatus] ?? status
  }

  severidadEstado(status: string): string {
    return STATUS_SEVERITY[status as AlgorithmConfigStatus] ?? 'info'
  }

  constructor(
    private readonly api: ApiAlgorithmConfigService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.cargar()
  }

  cargar(): void {
    this.cargando = true
    this.errorCarga = null
    this.api.getAll().subscribe((r) => {
      this.cargando = false
      if (r.error) {
        this.errorCarga = r.msg
        this.messageService.add({ severity: 'error', summary: 'No se pudo cargar', detail: r.msg })
        return
      }
      this.configs = r.data || []
      this.active = this.configs.find((c) => c.status === 'active') || null
    })
  }

  nueva(): void {
    this.router.navigate(['/algorithm-config/new'])
  }

  abrir(config: IAlgorithmConfig): void {
    this.router.navigate(['/algorithm-config', config._id])
  }

  //* Confirmación tipeada, no un Sí/No: activar cambia las tasas y los montos que la plataforma
  //* ofrece a partir de ese instante, y un clic de más no debería poder hacerlo.
  activar(config: IAlgorithmConfig, event: Event): void {
    event.stopPropagation()
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: `Activar la versión ${config.version}`,
      message: `A partir de ahora todas las ofertas nuevas se calcularán con "${config.name}". Las verificaciones ya emitidas no cambian: cada una guarda con qué versión se calculó.${this.active ? ` La versión ${this.active.version} quedará archivada.` : ''}`,
      icon: 'fa-solid fa-triangle-exclamation',
      acceptLabel: 'Activar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.api.activate(config._id).subscribe((r) => {
          if (r.error) {
            this.messageService.add({ severity: 'error', summary: 'No se pudo activar', detail: r.msg, life: 8000 })
            return
          }
          this.messageService.add({ severity: 'success', summary: 'Versión activada', detail: `Las ofertas nuevas ya usan la versión ${config.version}.` })
          this.cargar()
        })
      },
    })
  }

  borrar(config: IAlgorithmConfig, event: Event): void {
    event.stopPropagation()
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: 'Borrar el borrador',
      message: `Se elimina "${config.name}". Solo se borran borradores; las versiones activas y archivadas son historia que las verificaciones citan.`,
      icon: 'fa-solid fa-trash',
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.api.remove(config._id).subscribe((r) => {
          if (r.error) {
            this.messageService.add({ severity: 'error', summary: 'No se pudo borrar', detail: r.msg })
            return
          }
          this.messageService.add({ severity: 'success', summary: 'Borrador eliminado', detail: '' })
          this.cargar()
        })
      },
    })
  }

  autor(c: IAlgorithmConfig): string {
    const u = c.activatedBy || c.createdBy
    return u ? `${u.names} ${u.lastName}`.trim() : '—'
  }
}
